import {
  Form,
  FormInstance,
  Input,
  InputNumber,
  Modal,
  Select,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import useGetRequest from "../../hooks/useGetRequest";
import { Cashbox } from "../../types/cashbox.type";
import { Currency } from "../../types/currency.type";
import { Machine } from "../../types/machine.type";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import { User, UserRole } from "../../types/user.type";

const { Option } = Select;

interface CreateTransactionProps {
  title: string;
  open: boolean;
  form: FormInstance;
  confirmLoading: boolean;
  onCancel: () => void;
  onOk: () => void;
  onFinish: (values: {
    amount: number;
    cashboxId: string;
    exchangeRate: number;
    description: string;
    machineId: string;
    salesUserId?: string;
  }) => void;
  /** Необязательный список продажников для привязки транзакции */
  salesUsers?: User[];
}

const CreateTransaction: React.FC<CreateTransactionProps> = ({
  title,
  open,
  form,
  confirmLoading,
  onCancel,
  onOk,
  onFinish,
  salesUsers,
}) => {
  const { data: cashboxesList, loading } = useGetRequest(`/cashbox/list`);
  const { data: machineList, loading: machineListLoading } =
    useGetRequest("/machine/list");
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [selectedCashbox, setSelectedCashbox] = useState<Cashbox | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const { exchangeRate } = useExchangeRate();

  const handleCashboxChange = (value: string) => {
    const cashbox = cashboxesList?.find((cb: Cashbox) => cb._id === value);
    setSelectedCashbox(cashbox || null);
    setConvertedAmount(null);
  };

  const calculateConvertedAmount = (amount: number, exchangeRate: number) => {
    if (!selectedCashbox) return null;

    if (selectedCashbox.currency === Currency.US_DOLLAR) {
      return amount * exchangeRate;
    } else if (selectedCashbox.currency === Currency.UZBEK_SOM) {
      return amount / exchangeRate;
    }
    return null;
  };

  useEffect(() => {
    if (amount !== null && exchangeRate !== null && selectedCashbox) {
      const result = calculateConvertedAmount(amount, exchangeRate);
      setConvertedAmount(result);
    }
  }, [amount, exchangeRate, selectedCashbox]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const handleFinish = (values: any) => {
    onFinish({
      ...values,
      exchangeRate: exchangeRate || values.exchangeRate,
    });
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={confirmLoading}
    >
      <Form form={form} onFinish={handleFinish} layout="vertical">
        <Form.Item
          name="cashboxId"
          label="Касса"
          rules={[{ required: true, message: "Выберите кассу" }]}
        >
          <Select
            placeholder="Выберите кассу"
            loading={loading}
            onChange={handleCashboxChange}
          >
            {cashboxesList?.map((cashbox: Cashbox) => (
              <Option key={cashbox._id} value={cashbox._id}>
                Имя: {cashbox.name} | Валюта: {cashbox.currency}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="machineId"
          label="Станок"
          rules={[{ required: true, message: "Выберите станок" }]}
        >
          <Select
            placeholder="Выберите станок"
            allowClear
            loading={machineListLoading}
          >
            {machineList?.map((machine: Partial<Machine>) => (
              <Option key={machine._id} value={machine._id}>
                {machine.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Продажник (для привязки оборота/баланса), если список передан */}
        {salesUsers && salesUsers.length > 0 && (
          <Form.Item
            name="salesUserId"
            label="Продажник"
          >
            <Select placeholder="Выберите продажника" allowClear>
              {salesUsers
                .filter((u) => u.role === UserRole.SalesDept)
                .map((user: User) => (
                  <Option key={user._id} value={user._id}>
                    {user.name} ({user.username})
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="amount"
          label="Сумма"
          rules={[{ required: true, message: "Введите сумму" }]}
        >
          <InputNumber
            value={amount}
            onChange={(value) => setAmount(value)}
            min={0}
          />
        </Form.Item>

        <Form.Item
          name="exchangeRate"
          label="Курс"
          hidden={true}
        >
          <InputNumber value={exchangeRate} min={0} />
        </Form.Item>
        {convertedAmount !== null && (
          <Typography.Text>
            {selectedCashbox?.currency === Currency.US_DOLLAR
              ? Currency.UZBEK_SOM
              : Currency.US_DOLLAR}
            : {convertedAmount.toFixed(2)}
          </Typography.Text>
        )}
        <Form.Item
          name="description"
          label="Описание"
          rules={[{ required: true, message: "Введите описание." }]}
        >
          <Input.TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTransaction;
